import type { CSSProperties } from "react";
import { shopWindowTypes } from "@/modules/Constants";
import NextVideoBlock from "@/components/shopwindow/NextVideoBlock";
import UpNextList from "@/components/shopwindow/UpNextList";
import {
  isChainRevealed,
  isChainAnchoredOnMethodStep,
} from "@/modules/chainReveal";
import Favorites from "@/components/favorites/Favorites";
import Transition from "react-transition-group/Transition";
import CSSTransition from "react-transition-group/CSSTransition";
import MerchantCtaActionSheet from "@/components/shared/MerchantCtaActionSheet";
import PixieIntroTip from "@/components/player/PixieIntroTip";
import PixieSwitch from "@/components/player/PixieSwitch";
import PixieToast from "@/components/player/PixieToast";
import ShopWindow from "@/components/shopwindow/ShopWindow";
import {
  type ExperienceLayoutContext,
  PIXIE_BAR_HEIGHT,
  PIXIE_SWITCH_CLEARANCE,
  PIXIE_SWITCH_CLEARANCE_PX,
  panelSwitchLeft,
} from "@/components/player/experienceView";

/**
 * Portrait and desktop landscape: the video + shop-window split. A render
 * function, not a component: Experience returns this root div itself, so the
 * container node and every child slot stay where the class rendered them.
 */
export function renderSplitLayout(ctx: ExperienceLayoutContext) {
  const {
    view,
    experienceState,
    onApplicationEvent,
    header,
    shopWindow,
    video,
    controlBar,
    refs,
  } = ctx;
  const {
    sizes,
    isPortrait,
    shopWindowConfig,
    isLoaded,
    color,
    askConfig,
    askOpen,
    pixieClass,
    controlBarHeight,
    controlBarWidth,
    panelOpen,
  } = view;
  const experienceData = experienceState.experienceData;
  const applicationData = experienceState.applicationData;

  const containerX = sizes.container.margins.left;

  const offset =
    (sizes.panel.width + sizes.panel.margins.left + sizes.panel.margins.right) /
    2.0;

  const shopWindowPanelStyles: Record<string, number> = {
    entering: 0,
    entered: 0,
    exiting: -offset,
    exited: -offset,
  };

  const videoPanelStyles: Record<string, number> = {
    entering: 0,
    entered: 0,
    exiting: offset,
    exited: offset,
  };

  let favoritesStyles: Record<string, CSSProperties>;

  if (applicationData.isPortrait()) {
    favoritesStyles = {
      entering: { opacity: 1.0, transform: "translateY(0%)" },
      entered: { opacity: 1.0, transform: "translateY(0%)" },
      exiting: { opacity: 1.0, transform: "translateY(101%)" },
      exited: { opacity: 1.0, transform: "translateY(101%)" },
    };
  } else {
    const favoritesOffset = applicationData.showShopWindow() ? 0 : offset;
    const favoritesTransform = `translateX(${favoritesOffset}px)`;
    favoritesStyles = {
      entering: { opacity: 1.0, transform: favoritesTransform },
      entered: { opacity: 1.0, transform: favoritesTransform },
      exiting: { opacity: 0.0 },
      exited: { opacity: 0.0 },
    };
  }

  const showList =
    isLoaded &&
    applicationData.isPortrait() &&
    shopWindowConfig &&
    shopWindowConfig.isPortraitOverlay;
  // Portrait Method tab, chain anchored (prompt showing or list revealed):
  // the step rises to the top and the chain region fills below it. Drives both
  // the chain render and the panel modifier that keeps the step's
  // (absolute-child) wrapper from growing to absorb the panel's free space.
  const portraitChainAnchored =
    isLoaded &&
    isPortrait &&
    shopWindowConfig &&
    shopWindowConfig.type === shopWindowTypes.METHOD &&
    isChainAnchoredOnMethodStep(
      applicationData,
      shopWindowConfig,
      experienceData,
    );
  // Landscape pre-reveal window — the "Up Next" overlay prompt shows
  // on the last method step BEFORE the T−15s reveal (isChainActive is still
  // false, so the chain-end top-aligned layout hasn't kicked in). The overlay is
  // absolute-pinned to the panel bottom and, without this, floats over the step
  // with no way to scroll to the hidden lines. This flag top-aligns the step and
  // reserves the overlay's measured height (see method-shop-window.css +
  // NextVideoBlock's --pix-overlay-h) so the step always clears it.
  const landscapeOverlayActive =
    isLoaded &&
    !isPortrait &&
    shopWindowConfig &&
    shopWindowConfig.type === shopWindowTypes.METHOD &&
    isChainAnchoredOnMethodStep(
      applicationData,
      shopWindowConfig,
      experienceData,
    ) &&
    !isChainRevealed(applicationData);
  const hasActionSheet =
    applicationData.showArticleActionSheet() && applicationData.isPortrait();

  return (
    <div
      ref={ctx.setContainerEl}
      className={`experience-container${pixieClass}`}
      style={{
        width: sizes.container.width,
        height: sizes.container.height,
        WebkitTransform: `translate3d(${containerX}px, 0, 0)`,
        transform: `translate3d(${containerX}px, 0, 0)`,
        marginTop: sizes.container.margins.top,
      }}
    >
      <Transition
        nodeRef={refs.panel}
        in={applicationData.showShopWindow()}
        timeout={500}
      >
        {status => {
          const style: CSSProperties = !isPortrait
            ? ({
                backgroundColor: color,
                paddingTop: sizes.padding.top,
                paddingRight: sizes.padding.right,
                paddingBottom: sizes.padding.bottom,
                transform: `translate(${shopWindowPanelStyles[status]}px, 0)`,
                "--pixie-switch-clearance": PIXIE_SWITCH_CLEARANCE_PX,
              } as CSSProperties)
            : {
                backgroundColor: color,
              };
          return (
            <div
              key="shop-window-panel"
              ref={refs.panel}
              className={
                `shop-window-panel shop-window-panel-${status}` +
                (landscapeOverlayActive
                  ? " shop-window-panel--landscape-overlay"
                  : "") +
                (askConfig && !isPortrait
                  ? " shop-window-panel--pixie-switch"
                  : "")
              }
              style={style}
            >
              {/* In portrait the chain (Up Next list + inline
                  next-video block) fills the panel below the video. Gated to
                  the Method tab only — on the Product/Ingredients tabs the
                  recommendations must not appear (they'd cover the product
                  image / ingredient list); those tabs show their content full
                  size instead. The inline block owns the countdown +
                  auto-advance, so auto-advance runs while the Method tab is
                  active (the default view). Landscape renders its own
                  compact chain prompt over the video (LandscapeChainPrompt),
                  not inside the shop window.

                  Rendered from the moment the "Up Next" prompt appears (last
                  method step) — not only at the T−15s reveal — so the step
                  (anchored to the top by the same condition) and the prompt
                  read as one block. Before the reveal only the inline prompt
                  shows (the list stays hidden); `--prompt-only` then sizes the
                  region to the prompt so it sits directly under the step.

                  The step and the chain share ONE scroll region (the
                  `.portrait-chain-scroll` wrapper) so the last step scrolls
                  away together with the recs — like the desktop/landscape
                  single-scroll layout — instead of the step staying pinned
                  while only the chain scrolls. The control bar + overlay stay
                  outside the wrapper so they don't scroll with the content. */}
              {portraitChainAnchored ? (
                <div className="portrait-chain-scroll tw:[.portrait_&]:flex tw:[.portrait_&]:min-h-0 tw:[.portrait_&]:flex-[1_1_auto] tw:[.portrait_&]:flex-col tw:[.portrait_&]:overflow-y-auto tw:[.portrait_&]:overflow-x-hidden">
                  {shopWindow}
                  <div
                    className={
                      "shop-window--chain-end portrait-chain tw:[.portrait_&]:min-h-0 tw:[.portrait_&]:flex-[1_1_auto]" +
                      (isChainRevealed(applicationData)
                        ? ""
                        : " portrait-chain--prompt-only tw:[.portrait_&]:flex tw:[.portrait_&]:flex-col tw:[.portrait_&]:justify-end")
                    }
                  >
                    <UpNextList
                      experienceState={experienceState}
                      onApplicationEvent={onApplicationEvent}
                    />
                    <NextVideoBlock
                      experienceState={experienceState}
                      onApplicationEvent={onApplicationEvent}
                    />
                  </div>
                </div>
              ) : (
                shopWindow
              )}
              {askConfig && isPortrait ? (
                <PixieSwitch
                  className="pixie-switch--bar tw:flex-none tw:p-[8px_12px_10px]"
                  style={{ height: PIXIE_BAR_HEIGHT }}
                  experienceState={experienceState}
                  onApplicationEvent={onApplicationEvent}
                />
              ) : null}
              {controlBar}
              {/* Desktop landscape: the overlay "Up Next" prompt for the
                  last-Method-step early reveal (before T−30s), yielding to
                  the inline NextVideoBlock (MethodShopWindow) once the recs
                  list reveals. Portrait has its own block inside
                  portrait-chain-scroll; mobile immersive uses
                  LandscapeChainPrompt — so scope this to desktop landscape. */}
              {isLoaded && !isPortrait ? (
                <NextVideoBlock
                  variant="overlay"
                  experienceState={experienceState}
                  onApplicationEvent={onApplicationEvent}
                />
              ) : null}
            </div>
          );
        }}
      </Transition>
      <Transition
        nodeRef={refs.videoPanel}
        in={applicationData.showShopWindow()}
        timeout={500}
      >
        {status => {
          const style: CSSProperties = !isPortrait
            ? {
                backgroundColor: color,
                width: sizes.video.width + sizes.video.margins.left,
                height: sizes.container.height,
                transform: `translate(${videoPanelStyles[status]}px, 0)`,
              }
            : {
                backgroundColor: color,
              };
          return (
            <div
              className="video-window-panel"
              style={style}
              ref={refs.videoPanel}
            >
              {header}
              {video}
              <PixieToast />
            </div>
          );
        }}
      </Transition>
      <Transition
        nodeRef={refs.favorites}
        in={applicationData.showFavorites()}
        mountOnEnter={true}
        unmountOnExit={true}
        timeout={500}
      >
        {status => (
          <Favorites
            nodeRef={refs.favorites}
            style={favoritesStyles[status]}
            experienceState={experienceState}
            onApplicationEvent={onApplicationEvent}
          />
        )}
      </Transition>
      <CSSTransition
        nodeRef={refs.list}
        classNames="shop-window"
        in={showList}
        mountOnEnter={true}
        unmountOnExit={true}
        timeout={300}
      >
        <ShopWindow
          nodeRef={refs.list}
          style={{
            marginBottom: controlBarHeight + (askConfig ? PIXIE_BAR_HEIGHT : 0),
          }}
          experienceState={experienceState}
          hasHeader={true}
          hasExitButton={true}
          // biome-ignore lint/suspicious/noDoubleEquals: an undefined showList is not frozen
          freeze={showList == false}
          isOverlay={true}
          askLayout="sheet"
          onApplicationEvent={onApplicationEvent}
        />
      </CSSTransition>
      {!isPortrait && askConfig
        ? ctx.renderPixieWorkspace(askOpen, {
            top: sizes.padding.top,
            left: sizes.video.margins.left,
            bottom: sizes.padding.bottom,
            right: sizes.padding.right + controlBarWidth,
            // How wide the centred chat may be before it runs under the
            // switch (the composer shares the switch's row).
            "--pixie-chat-room": `${Math.max(
              0,
              panelSwitchLeft(sizes, controlBarWidth) -
                sizes.video.margins.left -
                12,
            )}px`,
          } as CSSProperties)
        : null}
      {/* At the foot of the recipe panel while that or Pixie is open; with the
          panel collapsed, the rail's Pixie button (ControlBar) is the way in
          instead. */}
      {!isPortrait && askConfig && panelOpen ? (
        <PixieSwitch
          className="pixie-switch--panel tw:absolute tw:z-5"
          style={{
            left: panelSwitchLeft(sizes, controlBarWidth),
            right: sizes.padding.right + controlBarWidth + 12,
            bottom: sizes.padding.bottom + 12,
          }}
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
        />
      ) : null}
      {askConfig && (isPortrait || applicationData.showShopWindow()) ? (
        <PixieIntroTip
          placement={isPortrait ? "portrait" : "desktop"}
          style={
            isPortrait
              ? { bottom: controlBarHeight + PIXIE_BAR_HEIGHT + 8 }
              : {
                  left: sizes.video.width + sizes.video.margins.left + 12,
                  right: sizes.padding.right + controlBarWidth + 12,
                  bottom: sizes.padding.bottom + PIXIE_SWITCH_CLEARANCE + 6,
                }
          }
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
        />
      ) : null}
      <CSSTransition
        nodeRef={refs.actionSheet}
        classNames="action-sheet"
        in={hasActionSheet}
        mountOnEnter={true}
        unmountOnExit={true}
        timeout={300}
      >
        <MerchantCtaActionSheet
          nodeRef={refs.actionSheet}
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
          hasHeader={true}
        />
      </CSSTransition>
    </div>
  );
}
